import { useUpdatePersonalProfile } from '@features/profile/hooks/useUpdatePersonalProfile';
import {
  PlayArrowOutlined,
  Close,
  // Videocam,
  NotificationsActive,
} from '@mui/icons-material';
import {
  Box,
  Container,
  css,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Grid,
  Button,
  IconButton,
  Alert,
} from '@mui/material';
import { staticFileLocation } from '@server/constants';
import { hireflixCta, hireflixCtaHover } from '@styles/colors';
import { bold, semibold } from '@styles/fontWeights';
import { FC, useState } from 'react';

import { hireflixFeatures } from './constants';

type FormData = {
  hireflix_onboarding: boolean;
};

type EmployerHireflixOnboardingModalProps = {
  setShowHireflixModal: (open: boolean) => void;
};

const EmployerHireflixOnboardingModal: FC<
  EmployerHireflixOnboardingModalProps
> = ({ setShowHireflixModal }) => {
  const [errorText, setErrorText] = useState('');
  const [showReminderModal, setShowReminderModal] = useState(false);

  const [isOnboardingModalOpen, setIsOnboardingModalOpen] =
    useState<boolean>(true);

  const handleCloseModal = () => {
    setIsOnboardingModalOpen(false);
  };

  const { updatePersonalProfile, isUpdatingPersonalProfile } =
    useUpdatePersonalProfile({
      setErrorText,
      onSuccess: () => {
        setShowReminderModal(false);
        handleCloseModal();
      },
    });

  const onSubmit = (values: FormData) => {
    updatePersonalProfile({
      hireflix_onboarding: values.hireflix_onboarding,
    });
  };

  return (
    <>
      <Dialog
        open={isOnboardingModalOpen}
        onClose={(event, reason) => {
          if (reason === 'backdropClick') {
            setShowReminderModal(true);
          }
        }}
        disableEscapeKeyDown
        disableEnforceFocus
        maxWidth="sm"
        fullWidth
        sx={{ '& .MuiPaper-root': { maxWidth: '816px !important' } }}
      >
        <DialogTitle sx={{ pt: 6 }}>
          <IconButton
            sx={{ position: 'absolute', top: 5, right: 5 }}
            onClick={() => setShowReminderModal(true)}
          >
            <Close />
          </IconButton>

          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            mt={2}
            mb={6}
          >
            <img
              src={`${staticFileLocation}img/logos/hire-flix.png`}
              alt="Logo"
              css={css`
                max-height: 25px;
                display: block;
              `}
            />
          </Box>
          <Typography
            fontWeight={bold}
            variant="h3"
            mb={2}
            sx={{ fontSize: { md: '26px' } }}
          >
            One-Way Video Interviews{' '}
            <Box component="span" sx={{ color: hireflixCta }}>
              Are Live!
            </Box>
          </Typography>
          <Typography
            sx={{
              maxWidth: '540px',
              width: '100%',
              margin: '0 auto',
              fontSize: '15px',
            }}
            textAlign="center"
            color="text.secondary"
          >
            Streamline your hiring with on-demand video interviews. Send invites
            with one click, let applicants record on their time, and review at
            your convenience.
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ py: '30px', px: { md: '35px' } }}>
          {errorText && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {errorText}
            </Alert>
          )}

          <Container
            disableGutters
            sx={{
              maxWidth: '100% !important',
              mb: 4,
              py: 4,
            }}
          >
            <Grid container spacing={3}>
              {hireflixFeatures.map((item) => (
                <Grid item xs={12} sm={6} key={item.id} display="flex">
                  <Box
                    component="article"
                    sx={{
                      padding: '16px',
                      border: '1px solid #e1e7ef',
                      borderRadius: '12px',
                      width: '100%',
                      transition: 'all 0.35s ease',
                      boxShadow:
                        'rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 1px 2px 0px',
                      '&:hover': {
                        boxShadow:
                          'rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.1) 0px 4px 6px -4px',
                        borderColor: hireflixCta,
                        transform: 'translateY(-.25rem)',

                        '& .icnWrap': {
                          backgroundColor: '#a24af530',
                        },
                      },
                    }}
                  >
                    <Box component="div" sx={{ display: 'flex', gap: '12px' }}>
                      <Box
                        component="div"
                        className="icnWrap"
                        sx={{
                          width: '40px',
                          height: '40px',
                          backgroundColor: '#a855f71a',
                          transition: 'background-color 0.35s ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '12px',
                          flexShrink: 0,
                        }}
                      >
                        {item.icon}
                      </Box>
                      <Box>
                        <Typography
                          variant="h6"
                          fontSize="16px"
                          lineHeight="16px"
                          fontWeight={600}
                          color="#001f3d"
                          mb="8px"
                          pt={1}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          lineHeight="20px"
                          color="#65758b"
                        >
                          {item.description}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>

          <Box component="div" textAlign="center" mb={2}>
            <Box
              component="div"
              justifyContent="center"
              gap={2}
              sx={{
                display: { xs: 'flex' },
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              <Button
                variant="contained"
                color="primary"
                startIcon={<PlayArrowOutlined />}
                href="/employer/hireflix-interviews/"
                sx={{
                  backgroundColor: hireflixCta,
                  '&:hover': {
                    backgroundColor: hireflixCtaHover,
                  },
                }}
                disabled={isUpdatingPersonalProfile}
              >
                Get Started with Video Interviews
              </Button>
              {/* <Button
                startIcon={<Videocam />}
                variant="outlined"
                color="primary"
                sx={{
                  textTransform: 'none',
                  borderColor: hireflixCta,
                  color: hireflixCta,
                  transition: 'all 0.35s ease',
                  '&:hover': {
                    backgroundColor: 'rgb(243, 232, 255)',
                    borderColor: hireflixCta,
                  },
                  '&.Mui-disabled': {
                    borderColor: 'rgba(0, 0, 0, 0.25) !important',
                  },
                }}
                disabled
              >
                See it in Action
              </Button> */}
            </Box>

            <Typography
              variant="body2"
              fontSize="13px"
              color="text.secondary"
              mt={2}
            >
              Setup takes only minutes - Try it completely free for 30 days, no
              obligation!
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Reminder Modal */}
      <Dialog
        open={showReminderModal}
        maxWidth="xs"
        fullWidth
        disableEnforceFocus
        disableEscapeKeyDown
        onClose={(event, reason) => {
          if (reason === 'backdropClick') return;
          setShowReminderModal(false);
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pt: 5 }}>
          <NotificationsActive sx={{ fontSize: '37px', color: hireflixCta }} />
          <Typography variant="h3" sx={{ pt: 1, fontWeight: semibold }}>
            Remind you later?
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pb: 5 }}>
          <Typography color="text.secondary" mb={3}>
            Would you like us to remind you to set up video interviews next time
            you log in?
          </Typography>

          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            gap={2}
            mt={5}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setShowReminderModal(false);
                setShowHireflixModal(false);
              }}
              disabled={isUpdatingPersonalProfile}
              sx={{
                backgroundColor: hireflixCta,
                '&:hover': {
                  backgroundColor: hireflixCtaHover,
                },
              }}
            >
              Yes, remind me later
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => {
                onSubmit({ hireflix_onboarding: true });
              }}
              disabled={isUpdatingPersonalProfile}
              sx={{
                textTransform: 'none',
                borderColor: hireflixCta,
                color: hireflixCta,
                transition: 'all 0.35s ease',
                '&:hover': {
                  backgroundColor: 'rgb(243, 232, 255)',
                  borderColor: hireflixCta,
                },
                '&.Mui-disabled': {
                  borderColor: 'rgba(0, 0, 0, 0.25) !important',
                },
              }}
            >
              No, I won’t use video interviews
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EmployerHireflixOnboardingModal;
